import {memo} from 'react';
import type {ReactNode} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type {StaticImageData} from 'next/image';
import DisplayImage from '@/components/Ui-components/display-image';
import {borderRadius, colors, fontSize, fontWeight, gradients, lineHeight, overlay, spacing, transition} from '@/styles/tokens';
import {ICON_TILE_ICON_SIZE, iconTileImageBaseStyle, iconTileImageStyle, iconTileLightSx, iconTileSx} from '@/styles/iconTile';

export interface FeatureIconCardProps {
    /** An MUI icon element, or an image source for a CDN/SVG icon. */
    icon?: ReactNode | StaticImageData | string;
    title: string;
    /** Small accent line between title and description, e.g. "Free, No Commitment". */
    subtitle?: string;
    description: string;
    /**
     * Static emphasis — draws the blue border without a hover. Use for the one
     * card a section deliberately leads with.
     */
    highlighted?: boolean;
    /** Heading level for the card title. Defaults to h3. */
    titleComponent?: 'h3' | 'h4';
    /**
     * `inline` (default) sits the icon tile beside the title; `stacked` puts it on
     * its own row above, for taller cards that lead with the icon.
     */
    iconPlacement?: 'inline' | 'stacked';
    /**
     * `dark` (default) is the site-wide navy tile with a white icon; `light` is the
     * soft-blue tile with a blue icon, for cards on a tinted section background.
     */
    iconTone?: 'dark' | 'light';
    /**
     * Fills the whole card navy on hover and knocks the copy and icon tile to white.
     * Opt-in — the default hover only picks up the blue border.
     */
    hoverFill?: boolean;
}

// `width/height: 100%` so a row of cards in a flex Grid item comes out even —
// without it the card shrinks to its content inside `display: flex` grid items.
const cardSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    width: '100%',
    height: '100%',
    gap: spacing[8],
    p: spacing[16],
    borderRadius: borderRadius.lg,
    bgcolor: colors.background.default,
    border: `1px solid ${colors.neutral.borderLight}`,
    transition: 'border-color 0.2s ease',
    '&:hover': {
        borderColor: colors.primary.main
    }
} as const;

const highlightedCardSx = {...cardSx, borderColor: colors.primary.main} as const;

// The copy is re-coloured from the card, not per-Typography: a descendant selector
// outranks each child's own `sx` colour, so one rule flips the whole card and every
// text style keeps a single source of truth for its resting colour.
const hoverFillSx = {
    transition: `background ${transition.medium}, border-color ${transition.medium}`,
    '&:hover': {
        background: gradients.darkSection,
        borderColor: 'transparent',
        '& .MuiTypography-root': {color: colors.text.inverse},
        // The tile has to lift off the navy it now sits on; monochrome image icons are
        // knocked out to white the same way the dark tile does it.
        '& .FeatureIconCard-tile': {
            background: overlay.whiteFaint,
            color: colors.text.inverse,
            '& img': {filter: 'brightness(0) invert(1)'}
        }
    }
} as const;

const cardHoverFillSx = {...cardSx, ...hoverFillSx} as const;

const highlightedHoverFillSx = {...highlightedCardSx, ...hoverFillSx} as const;

const resolveCardSx = (highlighted?: boolean, hoverFill?: boolean) => {
    if (hoverFill) return highlighted ? highlightedHoverFillSx : cardHoverFillSx;
    return highlighted ? highlightedCardSx : cardSx;
};

/** The tile stays fixed while a wrapping title grows beside it. */
const headerSx = {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: spacing[12]
} as const;

const stackedHeaderSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    gap: spacing[16]
} as const;

const subtitleSx = {
    fontSize: fontSize[13],
    fontWeight: fontWeight.semibold,
    color: colors.primary.main,
    lineHeight: lineHeight[20]
} as const;

const titleSx = {
    fontSize: fontSize[18],
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    lineHeight: lineHeight[28]
} as const;

const descSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[28]
} as const;

// An image source is a string or a next/image import; anything else is a React node.
const isImageSource = (icon: FeatureIconCardProps['icon']): icon is StaticImageData | string =>
    typeof icon === 'string' || (typeof icon === 'object' && icon !== null && 'src' in icon);

/**
 * FeatureIconCard (Molecule) — icon tile + title + description, the card shape
 * used across feature grids and stacks site-wide.
 */
const FeatureIconCard = ({
    icon,
    title,
    subtitle,
    description,
    highlighted,
    titleComponent = 'h3',
    iconPlacement = 'inline',
    iconTone = 'dark',
    hoverFill
}: FeatureIconCardProps) => (
    <Box sx={resolveCardSx(highlighted, hoverFill)}>
        <Box sx={iconPlacement === 'stacked' ? stackedHeaderSx : headerSx}>
            {icon && (
                <Box className="FeatureIconCard-tile" sx={iconTone === 'light' ? iconTileLightSx : iconTileSx}>
                    {isImageSource(icon) ? (
                        <DisplayImage
                            src={icon}
                            alt=""
                            width={ICON_TILE_ICON_SIZE}
                            height={ICON_TILE_ICON_SIZE}
                            style={iconTone === 'light' ? iconTileImageBaseStyle : iconTileImageStyle}
                        />
                    ) : (
                        (icon as ReactNode)
                    )}
                </Box>
            )}
            <Box>
                <Typography component={titleComponent} className="font-headers" sx={titleSx}>
                    {title}
                </Typography>
                {subtitle && (
                    <Typography component="p" className="font-headers" sx={subtitleSx}>
                        {subtitle}
                    </Typography>
                )}
            </Box>
        </Box>
        <Typography className="font-regular-text" sx={descSx}>
            {description}
        </Typography>
    </Box>
);

export default memo(FeatureIconCard);
